import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { BankDebitCandidate } from './payouts.service';

/** `returned` (0086): real money that arrived and was sent back. It stays in the
 *  cash proof — unlike `voided`, which means the row was never real money. `payout_return`
 *  (0201): the money of an executed payout the bank sent back, tied to it by two people. */
export type BankTransactionStatus = 'unmatched' | 'matched' | 'voided' | 'returned' | 'payout_return';

/** A void somebody asked for and no manager has decided yet. */
export interface VoidRequest {
  reason: string;
  requested_by: string;
  requested_by_name: string;
  requested_at: string;
  /** The reader asked for it: they may take it back and may not confirm it. */
  requested_by_me: boolean;
}

export interface BankTransaction {
  id: string;
  source: string;
  concentration_account_id: string;
  posted_at: string;
  amount: string;
  currency_code: string;
  sender_name: string | null;
  sender_account: string | null;
  sender_account_sha256: string | null;
  reference_text: string | null;
  status: BankTransactionStatus;
  matched_to_request_id: string | null;
  matched_by: string | null;
  matched_at: string | null;
  match_reason: string | null;
  created_at: string;
  void_request: VoidRequest | null;
  /** On hold for compliance: it cannot be sent back, voided or attributed until compliance decides. */
  blocked: boolean;
  /** The amount exactly as the bank printed it, present ONLY when it had a fraction of a cent: `amount` is then
   *  truncated to the cent (M-34) and the line waits for a manager; it is never matched automatically. */
  original_amount?: string | null;
  /** credit = money in, debit = money out. The attribution queue lists credits only (#311). */
  direction?: 'credit' | 'debit';
  /** The code the matching rules recorded when they left the line in the queue (#311). */
  auto_match_refusal?: string | null;
}

export interface RecordBankTransactionPayload {
  concentration_account_id: string;
  posted_at: string;
  amount: string;
  currency_code: string;
  sender_name?: string | null;
  sender_account?: string | null;
  reference_text?: string | null;
}

export interface AttributePayload {
  user_id: string;
  product_id: string;
  match_reason: string;
  notes?: string | null;
  /** Match the wire to THIS already-pending (agent-proposed) request instead
   *  of creating a new one. The backend refuses (409) unless amount, currency,
   *  client and product all equal the wire's exactly. */
  deposit_request_id?: string | null;
  /** 0083: which of the client's registered accounts the wire came from, when the
   *  bank reported no originator and staff established it from the transfer
   *  receipt. Without it an unattributable wire cannot be credited at all. */
  source_client_bank_account_id?: string | null;
}

export interface AttributeResponse {
  deposit_request_id: string;
}

/** Why money could not be kept (0086). Mirrors chk_bank_return_reason. */
export type ReturnReason =
  | 'rail_not_accepted'
  | 'sender_not_clients_own'
  | 'sender_unknown'
  | 'blocked_country'
  | 'client_request'
  | 'other'
  /** Nobody identified it within the owner's limit (90 days, owner 2026-10-31). The server
   *  refuses it for a credit that has not reached the limit. */
  | 'unidentified';

export interface ReturnTransferPayload {
  reason: ReturnReason;
  instruction: string;
  /** What the bank charges for the return. It comes out of the amount, so the
   *  sender receives less than they sent, and the notice says so. */
  fee: string;
  /** Step-up code: the instruction is what a person at the bank acts on to send
   *  money out, so the route refuses a return without a fresh authenticator code. */
  mfa_code: string;
}

export interface ReturnRecorded {
  return_id: string;
  bank_transaction_id: string;
  /** False when the money came from an account nobody registered: the return is
   *  recorded, there is simply no one to write to. */
  client_identified: boolean;
  client_notified: boolean;
}

export interface OpenReturn {
  id: string;
  bank_transaction_id: string;
  reason: ReturnReason;
  instruction: string;
  decided_at: string;
  fee_amount: string;
  fee_currency: string | null;
  user_id: string | null;
  notice_sent_at: string | null;
  amount: string;
  currency_code: string;
  posted_at: string;
}

export interface ConcentrationAccountOption {
  id: string;
  market: string;
  currency: string;
  beneficiary: string;
  transfer_method: string;
}

class BankTransactionsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async record(body: RecordBankTransactionPayload): Promise<BankTransaction> {
    const res = await this.api.post<BankTransaction>(
      '/api/v1/ops/bank-transactions',
      body,
    );
    return res.data;
  }

  async listConcentrationAccounts(): Promise<ConcentrationAccountOption[]> {
    const res = await this.api.get<ConcentrationAccountOption[]>(
      '/api/v1/ops/bank-transactions/concentration-accounts',
    );
    return res.data;
  }

  async listUnmatched(limit = 50, offset = 0): Promise<BankTransaction[]> {
    const res = await this.api.get<BankTransaction[]>(
      '/api/v1/ops/bank-transactions/unmatched',
      { params: { limit, offset } },
    );
    return res.data;
  }

  async getById(txId: string): Promise<BankTransaction> {
    const res = await this.api.get<BankTransaction>(
      `/api/v1/ops/bank-transactions/${txId}`,
    );
    return res.data;
  }

  async attribute(
    txId: string,
    body: AttributePayload,
  ): Promise<AttributeResponse> {
    const res = await this.api.post<AttributeResponse>(
      `/api/v1/ops/bank-transactions/${txId}/attribute`,
      body,
    );
    return res.data;
  }

  /** Send an inbound credit back where it came from, and record that it happened.
   *  Manager-only; refuses anything already credited to a client (that leaves
   *  through the deposit-reversal path). */
  async returnTransfer(txId: string, body: ReturnTransferPayload): Promise<ReturnRecorded> {
    const res = await this.api.post<ReturnRecorded>(
      `/api/v1/ops/bank-transactions/${txId}/return`,
      body,
    );
    return res.data;
  }

  /** Returns decided but not yet seen leaving the account. */
  async listOpenReturns(limit = 100): Promise<OpenReturn[]> {
    const res = await this.api.get<OpenReturn[]>(
      `/api/v1/ops/bank-transactions/returns/open`,
      { params: { limit } },
    );
    return res.data;
  }

  /** The imported bank debits that can be this return leaving: the amount sent back, the
   *  credit's currency, the receiving entity's account, after the decision, tied to nothing,
   *  never one paid to a different account than the money came from. Manager only. */
  async returnDebitCandidates(returnId: string): Promise<BankDebitCandidate[]> {
    const res = await this.api.get<BankDebitCandidate[]>(
      `/api/v1/ops/bank-transactions/returns/${returnId}/debit-candidates`,
    );
    return res.data;
  }

  /** Name the outgoing debit that is this return leaving — picked from
   *  `returnDebitCandidates`, never typed. Written once. Manager only, a different
   *  manager from the one who decided the return, and it takes a step-up code. Answers 204. */
  async settleReturn(
    returnId: string,
    debitBankTransactionId: string,
    mfaCode: string,
  ): Promise<void> {
    await this.api.post(`/api/v1/ops/bank-transactions/returns/${returnId}/settlement`, {
      debit_bank_transaction_id: debitBankTransactionId,
      mfa_code: mfaCode,
    });
  }
}

export default new BankTransactionsService();
